import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiMic,
  FiPlay,
  FiPause,
  FiDownload,
  FiLoader,
  FiAlertCircle,
  FiRefreshCw,
  FiVolume2,
  FiUser,
  FiFileText,
  FiClock,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { voiceService, isPassingFailure } from '../services/apiService';
import api from '../utils/api';
import { canPoll } from '../utils/requestGovernor';
import { useT } from '../utils/i18n';
import { useAuthStore } from '../utils/store';
import { useNavigate } from 'react-router-dom';

// Matches the backend's MAX_SCRIPT_CHARS (VoiceController.generateAudio) —
// raised from the old 20,000-char cap (~3,000-4,000 words) so a genuinely
// long script (30,000+ words) can go through in one shot instead of being
// rejected outright.
const MAX_SCRIPT_CHARS = 300000;

// How often to poll a generation job's status. A long script can take
// several minutes, so this doesn't need to be aggressive — 2s keeps the
// progress bar feeling live without generating excessive request volume
// over what could be a 10-20+ minute job.
const POLL_INTERVAL_MS = 2000;
// While the server can't be reached: ask again this often, and keep asking
// this long before leaving it to Audio History (a deploy takes a few minutes).
const POLL_RETRY_MS = 5000;
const POLL_GIVE_UP_MS = 15 * 60 * 1000;

// ─── Audio Player ─────────────────────────────────────────────────────────────

const AudioPlayer = ({ blobUrl, filename, onReset }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const togglePlay = () => {
    if (!audioRef.current) return;
    playing ? audioRef.current.pause() : audioRef.current.play();
    setPlaying((v) => !v);
  };

  const handleSeek = (e) => {
    const value = parseFloat(e.target.value);
    if (audioRef.current) { audioRef.current.currentTime = value; setCurrentTime(value); }
  };

  const formatTime = (s) => {
    if (!isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'generated-audio.mp3';
    a.click();
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center">
          <FiVolume2 size={16} className="text-violet-600" />
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">Generated Audio</p>
          <p className="text-xs text-gray-500">{filename || 'audio.mp3'}</p>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={blobUrl}
        onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
        onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
        onEnded={() => setPlaying(false)}
        className="hidden"
      />

      <div className="space-y-1">
        <input type="range" min={0} max={duration || 1} step={0.1} value={currentTime}
          onChange={handleSeek} className="w-full h-1.5 accent-violet-600 cursor-pointer" />
        <div className="flex justify-between text-[10px] text-gray-400">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={togglePlay}
          className="w-10 h-10 rounded-full bg-violet-600 hover:bg-violet-700 flex items-center justify-center text-white transition-colors flex-shrink-0">
          {playing ? <FiPause size={16} /> : <FiPlay size={16} />}
        </button>
        <button onClick={handleDownload}
          className="btn btn-ghost text-sm flex items-center gap-1.5 flex-1 justify-center border border-violet-200">
          <FiDownload size={14} /> Download MP3
        </button>
        <button onClick={onReset} title="Generate new audio"
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-white/60 transition-colors">
          <FiRefreshCw size={15} />
        </button>
      </div>
    </div>
  );
};

// ─── Inline mini-player used in history rows ──────────────────────────────────

const HistoryPlayer = ({ record, onDelete }) => {
  const audioRef  = useRef(null);
  const [playing, setPlaying]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [blobUrl, setBlobUrl]       = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration]     = useState(0);
  const [error, setError]           = useState(false);

  // Fetch audio from authenticated endpoint → blob URL
  const loadAudio = useCallback(async () => {
    if (blobUrl) return blobUrl;
    setLoading(true);
    setError(false);
    try {
      const token = localStorage.getItem('accessToken') || '';
      const baseURL = api.defaults?.baseURL || 'http://localhost:5001/api';
      const resp = await fetch(
        `${baseURL}/voice/audio/${record.filename}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error('Failed to load audio');
      const blob = await resp.blob();
      const url  = URL.createObjectURL(blob);
      setBlobUrl(url);
      return url;
    } catch (_) {
      setError(true);
      return null;
    } finally {
      setLoading(false);
    }
  }, [blobUrl, record.filename]);

  // Cleanup on unmount
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);

  const handlePlay = async () => {
    const url = await loadAudio();
    if (!url || !audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const handleDownload = async () => {
    const url = await loadAudio();
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${record.voiceName.replace(/[^a-z0-9]/gi, '_')}_${new Date(record.createdAt).getTime()}.mp3`;
    a.click();
  };

  const handleSeek = (e) => {
    const value = parseFloat(e.target.value);
    if (audioRef.current) { audioRef.current.currentTime = value; setCurrentTime(value); }
  };

  const formatTime = (s) => {
    if (!isFinite(s) || !s) return '';
    const m = Math.floor(s / 60);
    return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-3.5 space-y-2.5 hover:border-violet-200 transition-colors">
      {blobUrl && (
        <audio ref={audioRef} src={blobUrl}
          onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
          onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
          onEnded={() => setPlaying(false)}
          className="hidden" />
      )}

      {/* Header row */}
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
          <FiMic size={13} className="text-violet-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{record.voiceName}</p>
          <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{record.scriptPreview}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
            <FiClock size={9} /> {formatDate(record.createdAt)}
          </span>
        </div>
      </div>

      {/* Progress bar (only when loaded) */}
      {blobUrl && duration > 0 && (
        <input type="range" min={0} max={duration} step={0.1} value={currentTime}
          onChange={handleSeek}
          className="w-full h-1 accent-violet-500 cursor-pointer" />
      )}

      {/* Controls */}
      <div className="flex items-center gap-2">
        <button onClick={handlePlay} disabled={loading || error}
          className="w-7 h-7 rounded-full bg-violet-600 hover:bg-violet-700 disabled:opacity-40 flex items-center justify-center text-white transition-colors flex-shrink-0">
          {loading ? <FiLoader size={11} className="animate-spin" />
            : playing ? <FiPause size={11} /> : <FiPlay size={11} />}
        </button>

        {blobUrl && duration > 0 && (
          <span className="text-[10px] text-gray-400 tabular-nums">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          {record.filesize > 0 && (
            <span className="text-[10px] text-gray-400">{formatSize(record.filesize)}</span>
          )}
          <button onClick={handleDownload} disabled={loading || error}
            title="Download" className="p-1.5 text-gray-400 hover:text-violet-600 rounded-lg hover:bg-violet-50 transition-colors disabled:opacity-40">
            <FiDownload size={13} />
          </button>
          <button onClick={() => onDelete(record._id)}
            title="Delete" className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors">
            <FiTrash2 size={13} />
          </button>
        </div>

        {error && <span className="text-[10px] text-red-500">File unavailable</span>}
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

export const ScriptToVoice = () => {
  const t = useT();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const [artists, setArtists]                   = useState([]);
  const [loadingArtists, setLoadingArtists]     = useState(true);
  const [elVoices, setElVoices]                 = useState([]);
  const [loadingElVoices, setLoadingElVoices]   = useState(true);
  const [selectedVoice, setSelectedVoice]       = useState('');
  const ttsModel = 'eleven_v4';

  const [script, setScript]                     = useState('');
  const [generating, setGenerating]             = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationChunks, setGenerationChunks] = useState({ completed: 0, total: 0 });
  const [audioBlobUrl, setAudioBlobUrl]         = useState(null);
  const [audioFilename, setAudioFilename]       = useState('');
  const [error, setError]                       = useState('');
  const [reconnecting, setReconnecting]         = useState(false);

  const [history, setHistory]                   = useState([]);
  const [loadingHistory, setLoadingHistory]     = useState(true);
  const [historyOpen, setHistoryOpen]           = useState(true);

  // Guards the active poll loop so a stale one (component unmounted, or a
  // new generation started) can't keep updating state after it should've stopped.
  const pollGenerationRef = useRef(null);
  useEffect(() => () => { pollGenerationRef.current = null; }, []);

  const loadHistory = useCallback(async () => {
    try {
      const data = await voiceService.getAudioHistory();
      setHistory(data);
    } catch (_) {
      // Non-fatal
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    voiceService.listArtists()
      .then((data) => {
        setArtists(data);
        if (data.length > 0) setSelectedVoice(`artist:${data[0]._id}`);
      })
      .catch(() => toast.error('Failed to load voice artists.'))
      .finally(() => setLoadingArtists(false));

    voiceService.listElevenLabsVoices()
      .then(setElVoices)
      .catch(() => setElVoices([]))
      .finally(() => setLoadingElVoices(false));

    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (!loadingArtists && !loadingElVoices && !selectedVoice && elVoices.length > 0) {
      const v = elVoices[0];
      setSelectedVoice(`el:${v.voice_id}:${v.name}`);
    }
  }, [loadingArtists, loadingElVoices, elVoices, selectedVoice]);

  useEffect(() => () => { if (audioBlobUrl) URL.revokeObjectURL(audioBlobUrl); }, [audioBlobUrl]);

  const loading     = loadingArtists || loadingElVoices;
  const hasAnyVoice = artists.length > 0 || elVoices.length > 0;
  const charCount   = script.length;
  const wordCount   = script.trim() ? script.trim().split(/\s+/).length : 0;
  const estimatedDuration = Math.round(wordCount / 2.5);

  const selectedLabel = (() => {
    if (!selectedVoice) return '';
    if (selectedVoice.startsWith('artist:')) {
      const a = artists.find((x) => x._id === selectedVoice.slice(7));
      return a ? a.name : '';
    }
    const parts = selectedVoice.split(':');
    return parts.slice(2).join(':') || parts[1];
  })();

  const handleGenerate = async () => {
    if (!selectedVoice) { toast.error('Please select a voice.'); return; }
    if (!script.trim()) { toast.error('Please enter your script.'); return; }
    if (charCount > MAX_SCRIPT_CHARS) {
      toast.error(`Script must be under ${MAX_SCRIPT_CHARS.toLocaleString()} characters.`);
      return;
    }

    if (audioBlobUrl) { URL.revokeObjectURL(audioBlobUrl); setAudioBlobUrl(null); }

    setError('');
    setGenerating(true);
    setGenerationProgress(0);
    setGenerationChunks({ completed: 0, total: 0 });

    // Unique identity for this specific run — lets the poll loop notice if
    // it's been superseded (component unmounted, or the user somehow
    // triggers another generation) and stop touching state cleanly instead
    // of a stale loop clobbering a newer one's progress.
    const runToken = {};
    pollGenerationRef.current = runToken;

    try {
      let voiceArg;
      if (selectedVoice.startsWith('artist:')) {
        voiceArg = selectedVoice.slice(7);
      } else {
        const parts = selectedVoice.split(':');
        voiceArg = { voiceId: parts[1], voiceName: parts.slice(2).join(':') };
      }

      const job = await voiceService.startGeneration(voiceArg, script.trim(), ttsModel);
      if (pollGenerationRef.current !== runToken) return; // unmounted while starting
      setGenerationChunks({ completed: 0, total: job.totalChunks });

      // Poll until the job completes or fails. A 30,000-word script chunks
      // into ~200+ TTS calls and can genuinely take several minutes — this
      // loop just waits, it's not what's slow.
      //
      // A failed poll is NOT a failed job: the work runs on the server whether
      // or not this page can reach it. A dropped connection, a busy server or
      // a deploy just means "ask again shortly" — before, one such blip ended
      // the run with "Network Error" while the audio was still being made.
      let finalStatus = null;
      let unreachableSince = null;
      while (pollGenerationRef.current === runToken) {
        await new Promise((r) => setTimeout(r, unreachableSince ? POLL_RETRY_MS : POLL_INTERVAL_MS));
        if (pollGenerationRef.current !== runToken) break;
        if (!canPoll()) continue; // client-side budget tight — skip this tick, retry next cycle

        let status;
        try {
          status = await voiceService.getGenerationStatus(job.id);
        } catch (pollErr) {
          if (!isPassingFailure(pollErr)) throw pollErr;
          unreachableSince = unreachableSince || Date.now();
          setReconnecting(true);
          if (Date.now() - unreachableSince < POLL_GIVE_UP_MS) continue;
          // Out of reach for a long time: stop watching, but don't call it a
          // failure — the server may well finish it.
          if (pollGenerationRef.current === runToken) {
            toast.info('Lost connection to the server. Your voice-over will appear in Audio History below when it is ready.');
            loadHistory();
          }
          return;
        }
        if (pollGenerationRef.current !== runToken) break; // superseded while that request was in flight
        if (unreachableSince) { unreachableSince = null; setReconnecting(false); }

        setGenerationChunks({ completed: status.completedChunks, total: status.totalChunks });
        setGenerationProgress(status.percentage);

        if (status.status === 'completed' || status.status === 'failed') {
          finalStatus = status;
          break;
        }
      }

      if (!finalStatus) return; // superseded — a later run (or unmount) owns the UI now

      if (finalStatus.status === 'failed') {
        throw new Error(finalStatus.error || 'Audio generation failed.');
      }

      // Generation itself succeeded the moment status flipped to 'completed'
      // — the file is already safely on the server and in history regardless
      // of what happens next. Everything from here (fetching it to show the
      // inline player) is a separate, best-effort step: a large file over a
      // flaky connection can fail this download without the generation
      // having failed at all — confirmed live (server logged a correct 200 /
      // full Content-Length; the browser's fetch() still threw "Failed to
      // fetch" partway through). Don't let that read as "generation failed."
      loadHistory(); // the record is already there — refresh regardless of what follows
      try {
        const blob  = await voiceService.fetchAudioBlob(finalStatus.filename);
        const url   = URL.createObjectURL(blob);
        const fname = `${(selectedLabel || 'audio').replace(/[^a-z0-9]/gi, '_')}_${Date.now()}.mp3`;
        setAudioBlobUrl(url);
        setAudioFilename(fname);
        toast.success('Audio generated successfully!');
      } catch (previewErr) {
        toast.success('Audio generated successfully! Preview failed to load — open it from Audio History below instead.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Audio generation failed.';
      setError(msg);
      toast.error(msg);
    } finally {
      if (pollGenerationRef.current === runToken) {
        setReconnecting(false);
        setGenerating(false);
        setGenerationProgress(0);
        setGenerationChunks({ completed: 0, total: 0 });
        pollGenerationRef.current = null;
      }
    }
  };

  const handleReset = () => {
    if (audioBlobUrl) { URL.revokeObjectURL(audioBlobUrl); setAudioBlobUrl(null); }
    setAudioFilename('');
    setError('');
  };

  const handleDelete = async (id) => {
    try {
      await voiceService.deleteAudioHistory(id);
      setHistory((prev) => prev.filter((r) => r._id !== id));
      toast.success('Entry deleted.');
    } catch (_) {
      toast.error('Failed to delete entry.');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiMic size={22} className="text-violet-600" />
            Script to Voice
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Generate broadcast-quality Telugu audio from your script using trained voice artists.
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => navigate('/admin/voice-artists')}
            className="btn btn-ghost text-sm flex items-center gap-1.5 flex-shrink-0 border border-gray-200">
            <FiUser size={14} /> Manage Voice Artists
          </button>
        )}
      </div>

      {/* No voices warning */}
      {!loading && !hasAnyVoice && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <FiAlertCircle size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">No voices available.</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {isAdmin
                ? 'Configure your voice API key in Settings, then add voice artists.'
                : 'Ask your administrator to configure the voice settings.'}
            </p>
            {isAdmin && (
              <button onClick={() => navigate('/admin/voice-artists')}
                className="mt-2 text-xs text-amber-700 font-semibold hover:underline">
                Open Voice Artist Manager →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main generation form */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Left: Inputs */}
        <div className="lg:col-span-3 space-y-4">

          {/* Voice selector */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <FiUser size={14} className="text-violet-500" />
              Voice
            </label>
            {loading ? (
              <div className="input w-full flex items-center gap-2 text-gray-400 text-sm">
                <FiLoader size={13} className="animate-spin" /> Loading voices…
              </div>
            ) : (
              <select value={selectedVoice} onChange={(e) => setSelectedVoice(e.target.value)}
                className="input w-full text-sm" disabled={generating || !hasAnyVoice}>
                {!hasAnyVoice && <option value="">No voices available</option>}
                {artists.length > 0 && (
                  <optgroup label="Voice Artists (Trained)">
                    {artists.map((a) => (
                      <option key={a._id} value={`artist:${a._id}`}>
                        {a.name}{a.description ? ` — ${a.description}` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                {elVoices.length > 0 && (
                  <optgroup label="My Voices">
                    {elVoices.map((v) => (
                      <option key={v.voice_id} value={`el:${v.voice_id}:${v.name}`}>
                        {v.name}{v.category && v.category !== 'cloned' ? ` (${v.category})` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
            {selectedVoice && (
              <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                <FiMic size={10} />
                Telugu optimised
              </p>
            )}
          </div>


          {/* Script input */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <FiFileText size={14} className="text-violet-500" />
              Script
            </label>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="పేస్ట్ చేయండి లేదా మీ స్క్రిప్ట్ ఇక్కడ టైప్ చేయండి…&#10;&#10;Paste or type your Telugu news script here…"
              rows={12}
              className="input w-full text-sm resize-none leading-relaxed"
              disabled={generating}
              maxLength={MAX_SCRIPT_CHARS}
              style={{ fontFamily: "'Noto Sans Telugu', 'Noto Sans', sans-serif" }}
            />
            <div className="flex items-center justify-between mt-1 text-xs text-gray-400">
              <span>{wordCount.toLocaleString()} words · ~{estimatedDuration}s estimated</span>
              <span className={charCount > MAX_SCRIPT_CHARS * 0.9 ? 'text-orange-500 font-medium' : ''}>
                {charCount.toLocaleString()} / {MAX_SCRIPT_CHARS.toLocaleString()}
              </span>
            </div>
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-700">
              <FiAlertCircle size={15} className="flex-shrink-0 mt-0.5" /><span>{error}</span>
            </div>
          )}

          <button onClick={handleGenerate}
            disabled={generating || !selectedVoice || !script.trim() || !hasAnyVoice}
            className="btn btn-primary w-full flex items-center justify-center gap-2 text-sm py-2.5"
            style={{ background: generating ? undefined : 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
            {generating ? (
              <><FiLoader size={15} className="animate-spin" />
                {generationChunks.total > 0 ? `Generating… ${generationProgress}%` : 'Starting…'}</>
            ) : (
              <><FiVolume2 size={15} /> Generate Audio</>
            )}
          </button>

          {generating && (
            <div className="space-y-1.5">
              <div className="h-2 bg-violet-100 rounded-full overflow-hidden">
                <div
                  className={`h-full bg-violet-500 rounded-full ${generationChunks.total > 0 ? 'transition-all duration-500' : 'animate-pulse'}`}
                  style={{ width: generationChunks.total > 0 ? `${generationProgress}%` : '8%' }}
                />
              </div>
              <p className="text-xs text-gray-400 text-center">
                {generationChunks.total > 0
                  ? `Generating high-quality Telugu audio… ${generationChunks.completed} / ${generationChunks.total} segments (${generationProgress}%)`
                  : 'Starting generation…'}
              </p>
              {reconnecting && (
                <p className="text-[11px] text-amber-600 text-center">
                  Connection to the server dropped — reconnecting. Your voice-over keeps being made in the meantime.
                </p>
              )}
              {generationChunks.total > 20 && (
                <p className="text-[11px] text-gray-400 text-center">
                  Long scripts can take several minutes — feel free to leave this tab open in the background.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Right: Output + Tips */}
        <div className="lg:col-span-2 space-y-4">
          {audioBlobUrl ? (
            <AudioPlayer blobUrl={audioBlobUrl} filename={audioFilename} onReset={handleReset} />
          ) : (
            <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center text-gray-400 flex flex-col items-center justify-center min-h-[180px]">
              <FiVolume2 size={28} className="mb-2 opacity-40" />
              <p className="text-sm">Generated audio will appear here</p>
              <p className="text-xs mt-1">MP3 · 44.1 kHz · 192 kbps</p>
            </div>
          )}

          <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 space-y-2 text-xs text-gray-600">
            <p className="font-semibold text-gray-700">Tips for best quality</p>
            <ul className="space-y-1.5 ml-3 list-disc text-gray-500">
              <li>Write script in pure Telugu — avoid mixing English</li>
              <li>Use punctuation: commas, periods help natural pacing</li>
              <li>Abbreviations should be spelled out (e.g., కి.మీ → కిలోమీటర్లు)</li>
              <li>Numbers: write as words (100 → నూరు)</li>
              <li>For news, keep sentences short and declarative</li>
            </ul>
          </div>

          <div className="text-xs text-gray-400 space-y-0.5 px-1">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-violet-400" />Format: MP3 · 44.1 kHz · 192 kbps
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-violet-400" />Language: Telugu (te)
            </div>
          </div>
        </div>
      </div>

      {/* ── Audio History ─────────────────────────────────────────────────────── */}
      <div className="border border-gray-200 rounded-xl overflow-hidden">
        {/* History header */}
        <button
          onClick={() => setHistoryOpen((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
        >
          <div className="flex items-center gap-2">
            <FiClock size={15} className="text-violet-500" />
            <span className="text-sm font-semibold text-gray-800">Audio History</span>
            {history.length > 0 && (
              <span className="text-xs bg-violet-100 text-violet-700 font-medium px-1.5 py-0.5 rounded-full">
                {history.length} / 50
              </span>
            )}
          </div>
          {historyOpen ? <FiChevronUp size={15} className="text-gray-400" /> : <FiChevronDown size={15} className="text-gray-400" />}
        </button>

        {historyOpen && (
          <div className="p-3 space-y-2.5 bg-gray-50/50">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8 text-gray-400 gap-2 text-sm">
                <FiLoader size={14} className="animate-spin" /> Loading history…
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <FiVolume2 size={24} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No audio history yet.</p>
                <p className="text-xs mt-1">Generated audio files will appear here automatically.</p>
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-400 px-1">
                  Last 50 files are kept automatically. Oldest are deleted when limit is reached.
                </p>
                {history.map((record) => (
                  <HistoryPlayer key={record._id} record={record} onDelete={handleDelete} />
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
